import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { 
  registerUser, 
  loginUser, 
  logoutUser, 
  resetUserPassword, 
  updateUserProfile, 
  onAuthStateChange, 
  getCurrentUser 
} from '../services/firebaseService';

// Define the shape of the context
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  signup: (email: string, password: string, displayName: string) => Promise<User>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (displayName: string) => Promise<void>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  login: async () => { throw new Error('AuthContext not initialized'); },
  signup: async () => { throw new Error('AuthContext not initialized'); },
  logout: async () => { throw new Error('AuthContext not initialized'); },
  resetPassword: async () => { throw new Error('AuthContext not initialized'); },
  updateUserProfile: async () => { throw new Error('AuthContext not initialized'); }
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// Provider component that wraps the app and makes auth object available to any child component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Login function
  const login = async (email: string, password: string): Promise<User> => {
    return loginUser(email, password);
  };

  // Signup function
  const signup = async (email: string, password: string, displayName: string): Promise<User> => {
    const user = await registerUser(email, password, displayName);
    return user;
  };

  // Logout function
  const logout = async (): Promise<void> => {
    return logoutUser();
  };

  // Reset password function
  const resetPassword = async (email: string): Promise<void> => {
    return resetUserPassword(email);
  };

  // Update profile function
  const updateProfile = async (displayName: string): Promise<void> => {
    return updateUserProfile(displayName);
  };

  // Effect for handling auth state changes
  useEffect(() => {
    // Check if there's a current user on mount
    const user = getCurrentUser();
    setCurrentUser(user);

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // Context value
  const value = {
    currentUser,
    loading,
    login,
    signup,
    logout,
    resetPassword,
    updateUserProfile: updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="ss-boot" role="status" aria-live="polite">
          <p className="ss-kicker">Spin &amp; Solve</p>
          <p className="ss-brand">
            Spin <em>&amp;</em> Solve
          </p>
          <p className="ss-sub">Checking your sign-in.</p>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext; 